import { favorites } from '@/lib/favorites.js';

const READY_CLASS = 'cart-favorites-ready';
const HUB_POLL_INTERVAL_MS = 500;
const HUB_POLL_MAX = 60;
const SYNC_DELAYS_MS = [100, 600, 1400];
const FAV_LABEL_ADD = 'Add to favorites';
const FAV_LABEL_REMOVE = 'Remove from favorites';
let hubAddToCartBound = false;
let sawHubFavoriteIds = false;
const pendingByProductId = new Map();
const cartClickedIds = new Set();

const syncReadyState = () => {
  document.body.classList.toggle(READY_CLASS, favorites.isHubEnabled());
};

const readCartFavoriteProductId = container => {
  const fromContext = favorites.normalizeId(container.getAttribute('data-fav-pid'));
  if (fromContext) return fromContext;

  const widget = container.querySelector('[product-id], [data-product-id]');
  return favorites.normalizeId(widget?.getAttribute('product-id') || widget?.getAttribute('data-product-id'));
};

const readPdpFavoritedIds = () => {
  const ids = new Set();
  document.querySelectorAll('[data-pdp-favorite]').forEach(pdp => {
    if (!pdp.querySelector('.kl-hub-wishlist-icon--active')) return;
    const id = favorites.normalizeId(pdp.getAttribute('data-fav-pid'));
    if (id) ids.add(id);
  });
  return ids;
};

const noteHubFavoriteLists = () => {
  if (!window.customerHub) return;
  const lists = [window.customerHub.favorites, window.customerHub.wishlist, window.customerHub.favoriteProducts, window.customerHub.items];
  if (lists.some(list => Array.isArray(list) && list.length > 0)) sawHubFavoriteIds = true;
};

const reconcilePendingWithHub = () => {
  if (!sawHubFavoriteIds) return;
  pendingByProductId.forEach((wanted, id) => {
    if (favorites.isProductFavorited(id) === wanted) pendingByProductId.delete(id);
  });
};

const isCartProductFavorited = (productId, pdpFavoritedIds, icon) => {
  if (pendingByProductId.has(productId)) return pendingByProductId.get(productId);
  if (favorites.isProductFavorited(productId)) return true;
  if (!cartClickedIds.has(productId) && pdpFavoritedIds.has(productId)) return true;
  if (sawHubFavoriteIds) return false;
  return !!icon?.classList.contains('kl-hub-wishlist-icon--active');
};

const setCartFavoriteLabel = (container, favorited) => {
  const label = favorited ? FAV_LABEL_REMOVE : FAV_LABEL_ADD;
  container.setAttribute('aria-label', label);
  container.querySelectorAll('klaviyo-favorites-button, button, [role="button"]').forEach(el => {
    el.setAttribute('aria-label', label);
    el.shadowRoot?.querySelector('[aria-label], button')?.setAttribute('aria-label', label);
  });
};

const syncCartFavoriteVisuals = () => {
  noteHubFavoriteLists();
  reconcilePendingWithHub();
  const pdpFavoritedIds = readPdpFavoritedIds();

  document.querySelectorAll('[data-cart-favorite]').forEach(container => {
    const productId = readCartFavoriteProductId(container);
    if (!productId) return;

    const icon = container.querySelector('.kl-hub-wishlist-icon');
    if (!icon) return;

    const favorited = isCartProductFavorited(productId, pdpFavoritedIds, icon);
    icon.classList.toggle('kl-hub-wishlist-icon--active', favorited);
    icon.querySelector('svg')?.setAttribute('preserveAspectRatio', 'none');
    setCartFavoriteLabel(container, favorited);
  });
};

const hydrateVisibleCartFavorites = () => {
  document.querySelectorAll('[data-cart-favorite]').forEach(container => {
    const handle = container.getAttribute('data-fav-handle');
    if (handle) favorites.invokeHydrateProduct(handle);
  });
};

const scheduleSync = () => {
  SYNC_DELAYS_MS.forEach(delay => {
    window.setTimeout(() => {
      syncReadyState();
      if (document.body.classList.contains(READY_CLASS)) syncCartFavoriteVisuals();
    }, delay);
  });
};

const refreshStorefrontCart = () => {
  const request = window.theme?.getCart ? window.theme.getCart(true) : (window.theme?.fetchWithRetry || window.fetch).call(window, `${window.theme.routes.cart}.js`).then(response => response.json());

  request
    .then(cart => {
      document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart }, bubbles: true }));
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    })
    .catch(() => {});
};

const bindHubAddToCart = () => {
  const events = window.customerHubApi?.events;
  if (!events || hubAddToCartBound) return !!hubAddToCartBound;

  hubAddToCartBound = true;
  events.addEventListener('addtocart', refreshStorefrontCart);
  return true;
};

const pollForHub = () => {
  let attempts = 0;
  const tick = () => {
    if (favorites.isHubEnabled()) {
      syncReadyState();
      syncCartFavoriteVisuals();
      bindHubAddToCart();
    }
    if (++attempts < HUB_POLL_MAX) window.setTimeout(tick, HUB_POLL_INTERVAL_MS);
  };

  tick();
};

let favoritePress = null;

const hydrateFavoriteContext = container => {
  const context = favorites.readFavoriteContext(container);
  if (!context.productId) return null;

  favorites.setActiveProduct(context.productId, context.productUrl, context.imageUrl, context.productName);
  favorites.invokeHydrateProduct(context.handle);
  return context;
};

const snapshotFavoritePress = container => {
  const context = hydrateFavoriteContext(container);
  if (!context) {
    favoritePress = null;
    return;
  }

  favoritePress = {
    container,
    productId: context.productId,
    wasActive: !!container.querySelector('.kl-hub-wishlist-icon--active'),
    hubFav: favorites.isProductFavorited(context.productId),
    sawLists: sawHubFavoriteIds
  };
};

const commitFavoriteClick = container => {
  const context = hydrateFavoriteContext(container);
  if (!context) return;

  const snap = favoritePress && favoritePress.productId === context.productId && favoritePress.container === container ? favoritePress : null;
  favoritePress = null;

  const wasActive = snap ? snap.wasActive : !!container.querySelector('.kl-hub-wishlist-icon--active');
  const sawLists = snap ? snap.sawLists : sawHubFavoriteIds;
  const hubFav = snap ? snap.hubFav : favorites.isProductFavorited(context.productId);

  cartClickedIds.add(context.productId);
  pendingByProductId.set(context.productId, sawLists ? !hubFav : !wasActive);
  syncCartFavoriteVisuals();
  window.setTimeout(() => {
    syncCartFavoriteVisuals();
  }, 500);
};

export const initCartFavorites = drawer => {
  document.addEventListener('theme:cart:change', scheduleSync);
  document.addEventListener('theme:cart:reload', scheduleSync);
  drawer.addEventListener('theme:drawer:open', () => {
    hydrateVisibleCartFavorites();
    scheduleSync();
  });

  drawer.addEventListener(
    'pointerdown',
    e => {
      const container = e.target.closest('[data-cart-favorite]');
      if (!container || !drawer.contains(container)) return;
      snapshotFavoritePress(container);
    },
    true
  );

  drawer.addEventListener(
    'click',
    e => {
      const container = e.target.closest('[data-cart-favorite]');
      if (!container || !drawer.contains(container)) return;
      commitFavoriteClick(container);
    },
    true
  );

  drawer.addEventListener(
    'pointercancel',
    e => {
      const container = e.target.closest('[data-cart-favorite]');
      if (!container || favoritePress?.container !== container) return;
      favoritePress = null;
    },
    true
  );

  pollForHub();
};
